import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Drawer, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import DetailPanel from '../DetailPanel';
import { resolveEnrollmentRowCandidate } from '../../utils/detailRowResolver';
import { DrawerDragger } from './constants';

export const getDefaultDetailDrawerWidth = (viewportWidth) => {
  const safeViewportWidth = Math.max(0, Number(viewportWidth) || 0);
  return Math.min(safeViewportWidth, 1200, Math.max(640, Math.floor(safeViewportWidth * 0.6)));
};

export const useDetailDrawer = ({
  filteredData,
  navigateToDealer,
  onRowUpdated,
  isDetailFullScreen,
  appBarOffset,
}) => {
  const [detailRow, setDetailRow] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailDrawerWidth, setDetailDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return 1024;
    return getDefaultDetailDrawerWidth(window.innerWidth);
  });

  const resizeListenersRef = useRef({ move: null, up: null });
  const hasUserResizedRef = useRef(false);

  const cleanupResizeListeners = useCallback(() => {
    const { move, up } = resizeListenersRef.current;
    if (move) document.removeEventListener('mousemove', move, true);
    if (up) document.removeEventListener('mouseup', up, true);
    resizeListenersRef.current = { move: null, up: null };
  }, []);

  useEffect(() => cleanupResizeListeners, [cleanupResizeListeners]);

  const handleDetailMouseDown = useCallback(() => {
    if (isDetailFullScreen) return;
    const handleMouseMove = (event) => {
      const maxWidth = document.body.offsetWidth;
      const newWidth = Math.min(maxWidth, Math.max(400, maxWidth - event.clientX));
      hasUserResizedRef.current = true;
      setDetailDrawerWidth(newWidth);
    };

    const handleMouseUp = () => {
      cleanupResizeListeners();
    };

    resizeListenersRef.current = { move: handleMouseMove, up: handleMouseUp };
    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);
  }, [cleanupResizeListeners, isDetailFullScreen]);

  useEffect(() => {
    if (typeof window === 'undefined' || isDetailFullScreen) return undefined;
    const handleResize = () => {
      setDetailDrawerWidth((prev) => {
        const maxWidth = document.body.offsetWidth;
        if (hasUserResizedRef.current) {
          const clamped = Math.min(maxWidth, Math.max(Math.min(400, maxWidth), prev));
          if (Math.abs(prev - clamped) < 16) return prev;
          return clamped;
        }
        const target = getDefaultDetailDrawerWidth(window.innerWidth);
        if (Math.abs(prev - target) < 16) return prev;
        return target;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDetailFullScreen]);

  const resolveEnrollmentRow = useCallback(
    (candidate) => {
      return resolveEnrollmentRowCandidate(candidate, filteredData);
    },
    [filteredData]
  );

  const openDetail = useCallback(
    (row) => {
      if (!row) return;
      const resolved = resolveEnrollmentRow(row);
      if (resolved) {
        setDetailRow(resolved);
        setIsDetailOpen(true);
      }
    },
    [resolveEnrollmentRow]
  );

  const closeDetail = useCallback(() => {
    setIsDetailOpen(false);
  }, []);

  const handleDetailRowUpdated = useCallback(
    (updatedRow) => {
      if (!updatedRow) return;
      onRowUpdated?.(updatedRow);
      setDetailRow(updatedRow);
    },
    [onRowUpdated]
  );

  useEffect(() => {
    if (!isDetailOpen || !detailRow) return;
    const refreshed = resolveEnrollmentRow(detailRow);
    if (refreshed && refreshed !== detailRow) {
      setDetailRow(refreshed);
    }
  }, [isDetailOpen, detailRow, resolveEnrollmentRow]);

  useEffect(() => {
    if (isDetailOpen) return undefined;
    const timeout = setTimeout(() => setDetailRow(null), 200);
    return () => clearTimeout(timeout);
  }, [isDetailOpen]);

  const drawerPaperSx = useMemo(() => {
    if (isDetailFullScreen) {
      return {
        width: '100%',
        maxWidth: '100%',
        height: `calc(100% - ${appBarOffset}px)`,
        top: `${appBarOffset}px`,
        left: 0,
        right: 0,
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
      };
    }
    return {
      width: detailDrawerWidth,
      maxWidth: detailDrawerWidth,
      top: `${appBarOffset}px`,
      height: `calc(100% - ${appBarOffset}px)`,
      right: 0,
      position: 'fixed',
      display: 'flex',
      flexDirection: 'column',
    };
  }, [appBarOffset, detailDrawerWidth, isDetailFullScreen]);

  const detailDrawer = detailRow && (
    <Drawer
      anchor="right"
      open={Boolean(detailRow) && isDetailOpen}
      onClose={closeDetail}
      PaperProps={{
        sx: drawerPaperSx,
      }}
      ModalProps={{ keepMounted: true }}
    >
      {!isDetailFullScreen && <DrawerDragger onMouseDown={handleDetailMouseDown} />}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          p: { xs: 2, md: 3 },
          gap: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <IconButton onClick={closeDetail} aria-label="Close detail panel">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          data-testid="detail-drawer-content"
        >
          <DetailPanel
            rowData={detailRow}
            onNavigateToDealer={navigateToDealer}
            onRowUpdated={handleDetailRowUpdated}
            autoHydrateSelections
          />
        </Box>
      </Box>
    </Drawer>
  );

  return {
    detailRow,
    isDetailOpen,
    detailDrawer,
    openDetail,
    closeDetail,
  };
};
